/**
 * SendDelayConfig — retardo de envío de las plantillas de Thanks Order.
 *
 * El retardo NO se guarda en la plantilla: el `UpdateTemplateCommand` del backend no
 * propaga `extra_parameters`, y tocar una plantilla ya aprobada la devolvería a revisión
 * de Meta. Vive en el `config_values` de la skill, con una clave por plantilla.
 *
 * @module SendDelayConfig
 */

/** Techo de Google Cloud Tasks: no se puede programar nada a más de 30 días. */
export const MAX_DELAY_MINUTES = 30 * 24 * 60;

/** @enum {string} */
export const DELAY_UNITS = {
    MINUTES: 'minutes',
    HOURS: 'hours',
    DAYS: 'days',
};

/** @type {Object<string, number>} Minutos que vale cada unidad. */
const UNIT_IN_MINUTES = {
    [DELAY_UNITS.MINUTES]: 1,
    [DELAY_UNITS.HOURS]: 60,
    [DELAY_UNITS.DAYS]: 1440,
};

/**
 * Clave de `config_values` donde se guarda el retardo de una plantilla.
 *
 * Se deriva del nombre SIN el sufijo de idioma, así que todas las variantes de idioma de
 * una misma plantilla comparten retardo (el retardo es del aviso, no del idioma):
 *
 *   thanks_order_default_es  ->  delay_minutes_thanks_order_default
 *   restoo_confirmed_es      ->  delay_minutes_restoo_confirmed
 *
 * Se usa el nombre en crudo y no el tipo interpretado a propósito: hay plantillas en
 * producción con el tipo mal escrito (`thanks_order_banck_es`) y así front y n8n
 * coinciden siempre.
 *
 * ⚠️ El nodo `ResolveThanksDelay` del workflow `ThanksOrderScheduler` de n8n calcula
 * esta MISMA clave. Si cambia aquí, hay que cambiarla allí.
 *
 * @param {string} templateName
 * @returns {string}
 */
export function buildDelayKey(templateName) {
    const base = String(templateName || '')
        .trim()
        .toLowerCase()
        .replace(/(_[a-z]{2}(_[a-z]{2})?)+$/i, '');
    return `delay_minutes_${base || 'default'}`;
}

/**
 * Pasa minutos a la unidad más legible: 2880 -> `{ value: 2, unit: 'days' }`.
 *
 * @param {number|string} minutes
 * @returns {{ value: number, unit: string }}
 */
export function minutesToDelay(minutes) {
    const total = Math.max(0, Math.floor(Number(minutes) || 0));
    if (total === 0) return { value: 0, unit: DELAY_UNITS.HOURS };
    if (total % UNIT_IN_MINUTES[DELAY_UNITS.DAYS] === 0) {
        return { value: total / UNIT_IN_MINUTES[DELAY_UNITS.DAYS], unit: DELAY_UNITS.DAYS };
    }
    if (total % UNIT_IN_MINUTES[DELAY_UNITS.HOURS] === 0) {
        return { value: total / UNIT_IN_MINUTES[DELAY_UNITS.HOURS], unit: DELAY_UNITS.HOURS };
    }
    return { value: total, unit: DELAY_UNITS.MINUTES };
}

/**
 * Pasa valor + unidad a minutos, recortado al techo de Cloud Tasks.
 *
 * @param {number|string} value
 * @param {string} unit
 * @returns {number}
 */
export function delayToMinutes(value, unit) {
    const amount = Math.max(0, Math.floor(Number(value) || 0));
    return Math.min(amount * (UNIT_IN_MINUTES[unit] || 1), MAX_DELAY_MINUTES);
}

/**
 * Valor máximo admisible para una unidad, para no pasarse de los 30 días.
 *
 * @param {string} unit
 * @returns {number}
 */
export function maxValueForUnit(unit) {
    return Math.floor(MAX_DELAY_MINUTES / (UNIT_IN_MINUTES[unit] || 1));
}

/**
 * Etiqueta corta para la tabla y la vista previa.
 *
 * @param {number} minutes
 * @param {Function} t Función de traducción ($t)
 * @returns {string}
 */
export function formatDelay(minutes, t) {
    const total = Math.max(0, Math.floor(Number(minutes) || 0));
    if (total === 0) return t('thanksOrder.delay.immediate');
    const { value, unit } = minutesToDelay(total);
    return `+${value} ${t(`thanksOrder.delay.units.${unit}`)}`;
}
